import { OFFERS_DEAL_TYPES, OFFERS_PROPERTY_TYPES } from 'shared/constants/buildingLabels';
import type {
  IBuildingOfferCategory,
  IOffersFilters,
  TOffersDealType,
  TOffersPropertyType,
} from 'shared/types/buildingOffers';

/** Префикс категории API по типу помещения (`getCategoryType` монолита: `freeAppointment` ↔ `freeAppointmentObject`) */
const CATEGORY_PREFIXES: Record<TOffersPropertyType, string> = {
  office: 'office',
  shoppingArea: 'shoppingArea',
  warehouse: 'warehouse',
  freeAppointment: 'freeAppointmentObject',
};

const DEAL_SUFFIXES: Record<TOffersDealType, string> = { rent: 'Rent', sale: 'Sale' };

/** Категория API: `officeRent`, `freeAppointmentObjectSale` */
export const getOffersCategory = (dealType: TOffersDealType, propertyType: TOffersPropertyType): string =>
  `${CATEGORY_PREFIXES[propertyType]}${DEAL_SUFFIXES[dealType]}`;

/** Тип помещения по категории API; категории вне четырёх типов фильтра — null */
export const getCategoryPropertyType = (category: string): TOffersPropertyType | null =>
  OFFERS_PROPERTY_TYPES.find(({ type }) =>
    OFFERS_DEAL_TYPES.some(deal => getOffersCategory(deal.type, type) === category),
  )?.type ?? null;

export const findOfferCategory = (
  categories: IBuildingOfferCategory[],
  dealType: TOffersDealType,
  propertyType: TOffersPropertyType,
): IBuildingOfferCategory | undefined =>
  categories.find(({ category }) => category === getOffersCategory(dealType, propertyType));

/** Типы, у которых есть категория для сделки, — в порядке кнопок фильтра */
export const getAvailablePropertyTypes = (
  categories: IBuildingOfferCategory[],
  dealType: TOffersDealType,
): TOffersPropertyType[] =>
  OFFERS_PROPERTY_TYPES.filter(({ type }) => findOfferCategory(categories, dealType, type)).map(({ type }) => type);

/** Пункты якорного меню: типы, у которых есть категория при любой сделке (`Serp.getAnchorsList`) */
export const getAnchorPropertyTypes = (categories: IBuildingOfferCategory[]): TOffersPropertyType[] =>
  OFFERS_PROPERTY_TYPES.filter(({ type }) =>
    OFFERS_DEAL_TYPES.some(deal => findOfferCategory(categories, deal.type, type)),
  ).map(({ type }) => type);

/** Вкладка по умолчанию: «От официального представителя», если у категории есть его объявления */
const getIsFromRepresentative = (
  categories: IBuildingOfferCategory[],
  dealType: TOffersDealType,
  propertyType: TOffersPropertyType | null,
): boolean =>
  propertyType !== null && (findOfferCategory(categories, dealType, propertyType)?.fromRepresentativeCount ?? 0) > 0;

const mkFilters = (
  categories: IBuildingOfferCategory[],
  dealType: TOffersDealType,
  propertyType: TOffersPropertyType | null,
): IOffersFilters => ({
  dealType,
  propertyType,
  isFromRepresentative: getIsFromRepresentative(categories, dealType, propertyType),
});

/** Фильтры по умолчанию: первая сделка и первый тип, для которых есть объявления (`Serp` монолита) */
export function getInitialOffersFilters(categories: IBuildingOfferCategory[]): IOffersFilters {
  const dealType =
    OFFERS_DEAL_TYPES.find(deal => getAvailablePropertyTypes(categories, deal.type).length)?.type ??
    OFFERS_DEAL_TYPES[0].type;

  return mkFilters(categories, dealType, getAvailablePropertyTypes(categories, dealType)[0] ?? null);
}

/** Смена сделки: тип сохраняется, если доступен для новой сделки, иначе — первый доступный (`setDealType`) */
export function changeOffersDealType(
  categories: IBuildingOfferCategory[],
  filters: IOffersFilters,
  dealType: TOffersDealType,
): IOffersFilters {
  const available = getAvailablePropertyTypes(categories, dealType);
  const propertyType =
    filters.propertyType && available.includes(filters.propertyType) ? filters.propertyType : (available[0] ?? null);

  return mkFilters(categories, dealType, propertyType);
}

export function changeOffersPropertyType(
  categories: IBuildingOfferCategory[],
  filters: IOffersFilters,
  propertyType: TOffersPropertyType,
): IOffersFilters {
  return mkFilters(categories, filters.dealType, propertyType);
}

/** Клик по якорю: сделка сохраняется, если у неё есть этот тип, иначе — другая (`Serp.scrollIntoView`) */
export function selectOffersAnchor(
  categories: IBuildingOfferCategory[],
  filters: IOffersFilters,
  propertyType: TOffersPropertyType,
): IOffersFilters {
  const dealType = findOfferCategory(categories, filters.dealType, propertyType)
    ? filters.dealType
    : (OFFERS_DEAL_TYPES.find(deal => findOfferCategory(categories, deal.type, propertyType))?.type ??
      filters.dealType);

  return mkFilters(categories, dealType, propertyType);
}
